import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { parseInteractiveIdForEdit } from "./parseInteractiveIdForEdit";
import { ContextMenu } from "primereact/contextmenu";
import { clampMenuToViewport } from "./chartContextMenuEvent.js";
// Lazy load heavy modal
const InteractiveEditModal = lazy(
  () => import("../../Modals/InteractiveEditModal"),
);

// Module-level state survives component unmount/remount (e.g., fitWidth resize cycle)
let _persistedModal = null; // { selectedInteractive, showEditModal }

const InteractiveContextMenu = forwardRef(
  ({ chartId, onDeleteInteractive, onEditInteractive }, ref) => {
    const contextMenuRef = useRef(null);
    // Dedup: multiple sections' DrawingObjectSelectors fire for the same right-click event.
    // Track the native event object so only the first call (from the main chart section) wins.
    const lastNativeEventRef = useRef(null);
    // PoC #popout: show is deferred one commit so the ContextMenu portal can
    // re-target appendTo to the clicked window's body before positioning.
    const [showRequest, setShowRequest] = useState(null);
    const showSeqRef = useRef(0);

    useEffect(() => {
      if (!showRequest) return;
      contextMenuRef.current?.show(showRequest.showEvent);
    }, [showRequest]);

    // Restore from module-level cache on mount, or use defaults
    const [selectedInteractive, setSelectedInteractive] = useState(
      () => _persistedModal?.selectedInteractive || null,
    );
    const [showEditModal, setShowEditModal] = useState(
      () => _persistedModal?.showEditModal || false,
    );

    // Persist modal state to module-level on changes
    useEffect(() => {
      if (showEditModal && selectedInteractive) {
        _persistedModal = { selectedInteractive, showEditModal };
      } else if (!showEditModal) {
        _persistedModal = null;
      }
    }, [showEditModal, selectedInteractive]);

    useImperativeHandle(
      ref,
      () => ({
        show: (item, moreProps, event) => {
          // Multiple InteractiveLayers (one per chart section) can fire show() for the same
          // right-click because DrawingObjectSelector uses GenericComponent which broadcasts
          // to all sections. The first call comes from the main chart section (the correct one).
          // Subsequent calls from indicator sections are duplicates — ignore them.
          const nativeEvent = event?.nativeEvent || event;
          if (nativeEvent && nativeEvent === lastNativeEventRef.current) {
            return;
          }
          lastNativeEventRef.current = nativeEvent;

          setSelectedInteractive({ ...item, moreProps });
          if (contextMenuRef.current) {
            // Get the native event
            const nativeEvent = event?.nativeEvent || event;

            // PoC #popout: the menu must render in the window where the user
            // right-clicked — derive its document from the event target.
            const targetDoc = nativeEvent?.target?.ownerDocument ?? document;

            // For touch events, we need to extract coordinates differently
            // Touch events have touches/changedTouches arrays instead of pageX/pageY
            let showEvent = nativeEvent;

            // Check if this is a touch event or if pageX/pageY are missing
            if (
              nativeEvent?.type?.startsWith("touch") ||
              nativeEvent?.touches ||
              nativeEvent?.changedTouches
            ) {
              // It's a touch event - try to get coords from changedTouches (touchend) or touches
              const touch =
                nativeEvent.changedTouches?.[0] || nativeEvent.touches?.[0];
              if (touch) {
                showEvent = {
                  pageX: touch.pageX,
                  pageY: touch.pageY,
                  clientX: touch.clientX,
                  clientY: touch.clientY,
                  preventDefault: () => nativeEvent?.preventDefault?.(),
                  stopPropagation: () => nativeEvent?.stopPropagation?.(),
                };
              }
            }

            // Fallback: use moreProps.mouseXY if available (chart-relative coordinates)
            // Convert to page coordinates using the chart's position
            if (
              (!showEvent?.pageX && !showEvent?.pageY) ||
              showEvent.pageX === undefined
            ) {
              if (moreProps?.mouseXY) {
                const [x, y] = moreProps.mouseXY;
                // Get chart container to calculate absolute position
                // (scoped to the window where the click happened)
                const view = targetDoc.defaultView ?? window;
                const chartContainer =
                  targetDoc.querySelector(".react-stockcharts");
                if (chartContainer) {
                  const rect = chartContainer.getBoundingClientRect();
                  showEvent = {
                    pageX: rect.left + x + view.scrollX,
                    pageY: rect.top + y + view.scrollY,
                    clientX: rect.left + x,
                    clientY: rect.top + y,
                    preventDefault: () => {},
                    stopPropagation: () => {},
                  };
                }
              }
            }

            if (showEvent?.pageX !== undefined) {
              // Suppress the browser context menu NOW — the actual show is
              // deferred one commit so the portal re-targets appendTo first.
              nativeEvent?.preventDefault?.();
              setShowRequest({
                showEvent,
                body: targetDoc.body,
                seq: ++showSeqRef.current,
              });
            }
          }
        },
        openEditModal: (interactiveId) => {
          const interactive = parseInteractiveIdForEdit(interactiveId);
          if (interactive) {
            setSelectedInteractive(interactive);
            setShowEditModal(true);
          }
        },
        // Open the edit modal for an already-resolved item (same shape as
        // show()). Used by the unified chart context menu ("Editar desenho") —
        // reuses this component's modal + persistence, no menu shown.
        openEditFor: (item, moreProps) => {
          if (!item) return;
          setSelectedInteractive({ ...item, moreProps });
          setShowEditModal(true);
        },
      }),
      [],
    );

    const handleEdit = useCallback(() => {
      setShowEditModal(true);
    }, []);

    const handleEditModalClose = useCallback(() => {
      setShowEditModal(false);
    }, []);

    const handleEditModalSave = useCallback(
      (interactiveId, updatedAppearance) => {
        if (onEditInteractive) {
          onEditInteractive(interactiveId, updatedAppearance);
        }
        setShowEditModal(false);
      },
      [onEditInteractive],
    );

    const handleDelete = useCallback(() => {
      if (selectedInteractive && onDeleteInteractive) {
        onDeleteInteractive(chartId, selectedInteractive);
      }
      setSelectedInteractive(null);
    }, [selectedInteractive, onDeleteInteractive, chartId]);

    const menuItems = useMemo(() => {
      return [
        {
          label: "Editar",
          icon: "pi pi-pencil",
          command: handleEdit,
        },
        {
          label: "Excluir",
          icon: "pi pi-trash",
          command: handleDelete,
        },
      ];
    }, [handleEdit, handleDelete]);

    return (
      <>
        <ContextMenu
          model={menuItems}
          ref={contextMenuRef}
          appendTo={showRequest?.body ?? document.body}
          baseZIndex={100000}
          onShow={() =>
            requestAnimationFrame(() =>
              clampMenuToViewport(contextMenuRef.current?.getElement?.()),
            )
          }
        />
        {showEditModal && selectedInteractive && (
          <Suspense fallback={<div></div>}>
            <InteractiveEditModal
              chartId={chartId}
              interactive={selectedInteractive}
              visible={showEditModal}
              onClose={handleEditModalClose}
              onSave={handleEditModalSave}
            />
          </Suspense>
        )}
      </>
    );
  },
);

InteractiveContextMenu.displayName = "InteractiveContextMenu";

InteractiveContextMenu.propTypes = {
  chartId: PropTypes.string.isRequired,
  onDeleteInteractive: PropTypes.func.isRequired,
  onEditInteractive: PropTypes.func.isRequired,
};

export default React.memo(InteractiveContextMenu);
